import React, { useState } from 'react';
import OverviewTab from './tabs/OverviewTab';
import CommunicationsTab from './tabs/CommunicationsTab';
import NotesTab from './tabs/NotesTab';
import DocumentsTab from './tabs/DocumentsTab';
import BillingTab from './tabs/BillingTab';
import CoCounselTab from './tabs/CoCounselTab';
import { Contact } from '../../../config/api';
import { useNavigate } from 'react-router-dom';
import { useClientBilling } from '../hooks/useClientBilling';
import type { AiPanelMode } from '../routes/ContactDashboard';

// Define tab keys as union type for type safety
type TabKey = 'overview' | 'communications' | 'notes' | 'documents' | 'billing' | 'co-counsel';

// Define tab configuration interface
interface TabConfig {
  key: TabKey;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
}

// Define contact status type for better type safety
type ContactStatus = 'lead' | 'qualified' | 'converted' | 'archived';

interface ContactDetailProps {
  contact: Contact;
  onClose: () => void;
  onUpdate: (updatedContact: Contact) => void;
  isQuickView?: boolean;
  aiPanelMode?: AiPanelMode;
  onToggleAiPanel?: () => void;
  onSetAiMode?: (mode: AiPanelMode) => void;
}

// Configure tabs — Bills + Transactions consolidated into single Billing tab
const TABS: TabConfig[] = [
  { key: 'overview', label: 'Overview', component: OverviewTab },
  { key: 'communications', label: 'Communications', component: CommunicationsTab },
  { key: 'notes', label: 'Notes', component: NotesTab },
  { key: 'documents', label: 'Documents', component: DocumentsTab },
  { key: 'billing', label: 'Billing', component: BillingTab },
  { key: 'co-counsel', label: 'Co-Counsel', component: CoCounselTab },
];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const ContactDetail: React.FC<ContactDetailProps> = ({ contact, onClose, onUpdate, isQuickView = false, aiPanelMode = 'collapsed', onToggleAiPanel, onSetAiMode }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const billing = useClientBilling(contact.id);
  const [formData, setFormData] = useState({
    name: contact.name,
    email: contact.email || '',
    phone: contact.phone || '',
    address: contact.address || '',
    status: contact.status,
    contact_type: contact.contact_type,
    tags: contact.tags || [],
    notes: contact.notes || '',
    company: (contact.custom_fields?.company as string) || '',
    title: (contact.custom_fields?.title as string) || '',
    dateOfBirth: (contact.custom_fields?.dateOfBirth as string) || '',
  });
  const [tagInput, setTagInput] = useState('');
  const navigate = useNavigate();

  // Update form data when contact changes
  React.useEffect(() => {
    setFormData({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || '',
      status: contact.status,
      contact_type: contact.contact_type,
      tags: contact.tags || [],
      notes: contact.notes || '',
      company: (contact.custom_fields?.company as string) || '',
      title: (contact.custom_fields?.title as string) || '',
      dateOfBirth: (contact.custom_fields?.dateOfBirth as string) || '',
    });
  }, [contact]);

  const handleSave = () => {
    // Merge company, title, dateOfBirth, contact_type into custom_fields
    const updatedCustomFields: Record<string, unknown> = {
      ...(contact.custom_fields || {}),
    };
    if (formData.company.trim()) updatedCustomFields.company = formData.company.trim();
    else delete updatedCustomFields.company;
    if (formData.title.trim()) updatedCustomFields.title = formData.title.trim();
    else delete updatedCustomFields.title;
    if (formData.dateOfBirth) updatedCustomFields.dateOfBirth = formData.dateOfBirth;
    else delete updatedCustomFields.dateOfBirth;
    updatedCustomFields.contact_type = formData.contact_type;

    const { company, title, dateOfBirth, ...restFormData } = formData;
    onUpdate({
      ...contact,
      ...restFormData,
      custom_fields: updatedCustomFields,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || '',
      status: contact.status,
      contact_type: contact.contact_type,
      tags: contact.tags || [],
      notes: contact.notes || '',
      company: (contact.custom_fields?.company as string) || '',
      title: (contact.custom_fields?.title as string) || '',
      dateOfBirth: (contact.custom_fields?.dateOfBirth as string) || '',
    });
    setTagInput('');
    setIsEditing(false);
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove),
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const getStatusColor = (status: ContactStatus): string => {
    const statusColors: Record<ContactStatus, string> = {
      lead: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200',
      qualified: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
      converted: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200',
      archived: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200'
    };
    
    return statusColors[status] || statusColors.archived;
  };

  // Quick view markup
  const quickView = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">
              {contact.contact_type === 'company' ? '🏢' : '👤'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{contact.name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                {contact.contact_type} • {contact.status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Quick Info */}
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</span>
            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(contact.status as ContactStatus)}`}>
              {contact.status}
            </span>
          </div>

          {contact.email && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</span>
              <span className="text-sm text-gray-900 dark:text-white">{contact.email}</span>
            </div>
          )}

          {contact.phone && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone</span>
              <span className="text-sm text-gray-900 dark:text-white">{contact.phone}</span>
            </div>
          )}

          {contact.address && (
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Address</span>
              <p className="text-sm text-gray-900 dark:text-white mt-1">{contact.address}</p>
            </div>
          )}

          {contact.tags && contact.tags.length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tags</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {contact.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-lex-accent-soft text-lex-accent rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Created: {new Date(contact.created_at).toLocaleDateString()}</span>
            <span>Updated: {new Date(contact.updated_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-2">
            <button className="px-3 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors text-sm">
              Send Email
            </button>
            <button className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm">
              Schedule Call
            </button>
            <button className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
              Add Note
            </button>
            <button className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm">
              View Full
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Full detail as a page
  const fullDetailView = (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Slim Header — name + status + action buttons */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/contacts')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="text-xl font-semibold bg-transparent border-b-2 border-lex-accent text-gray-900 dark:text-white focus:outline-none w-full max-w-md"
                  />
                ) : (
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{contact.name}</h1>
                )}
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(contact.status as ContactStatus)}`}>
                  {contact.status}
                </span>
                <span className="text-sm text-gray-400">
                  {contact.contact_type === 'company' ? 'Company' : 'Individual'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onToggleAiPanel && (
              <div className="flex items-center gap-1">
                <button
                  onClick={onToggleAiPanel}
                  className={`px-3 py-1.5 border rounded-md text-sm transition-colors flex items-center gap-1.5 ${
                    aiPanelMode !== 'collapsed'
                      ? 'border-lex-accent bg-lex-accent-soft text-lex-accent'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                  </svg>
                  AI Insights
                </button>
                {/* Mode indicator chips */}
                {aiPanelMode !== 'collapsed' && onSetAiMode && (
                  <div className="flex border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                    <button
                      onClick={() => onSetAiMode('peek')}
                      className={`px-2 py-1.5 text-xs transition-colors ${aiPanelMode === 'peek' ? 'bg-lex-accent-soft text-lex-accent' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      title="Compact view"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25" /></svg>
                    </button>
                    <button
                      onClick={() => onSetAiMode('full')}
                      className={`px-2 py-1.5 text-xs transition-colors border-l border-gray-200 dark:border-gray-600 ${aiPanelMode === 'full' ? 'bg-lex-accent-soft text-lex-accent' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      title="Full view"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" /></svg>
                    </button>
                  </div>
                )}
              </div>
            )}
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSave} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                >
                  Save
                </button>
                <button onClick={handleCancel} className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Strip — always visible across all tabs */}
      <div className="flex-shrink-0 px-6 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-6 text-sm">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Outstanding</span>
            <div className="font-semibold text-red-600 dark:text-red-400 tabular-nums">{formatCurrency(billing.stats.billsOutstanding)}</div>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Trust Balance</span>
            <div className="font-semibold text-teal-600 dark:text-teal-400 tabular-nums">{formatCurrency(billing.stats.trustBalance)}</div>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Collection Rate</span>
            <div className="font-semibold text-green-600 dark:text-green-400 tabular-nums">{billing.stats.collectionRate.toFixed(0)}%</div>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Matters</span>
            <div className="font-semibold text-gray-900 dark:text-white tabular-nums">{billing.stats.matterCount}</div>
          </div>
        </div>
      </div>
        
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus:outline-none ${
              activeTab === tab.key
                ? 'border-lex-accent text-lex-accent'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* REMOVED: Old contact info grid + additional details moved to OverviewTab */}

      {/* Tab Content with entrance animation */}
      <div
        className={`flex-1 min-h-0 px-6 py-6 ${
          activeTab === 'notes' ? 'overflow-hidden' : 'overflow-y-auto'
        }`}
      >
        <div
          key={activeTab}
          className={`lex-tab-panel ${activeTab === 'notes' ? 'h-full min-h-0' : ''}`}
        >
        {(() => {
          const currentTab = TABS.find(tab => tab.key === activeTab);
          if (!currentTab) return <div>Tab not found</div>;

          const TabComponent = currentTab.component;

          if (activeTab === 'overview') {
            return <TabComponent contact={contact} billing={billing} isEditing={isEditing} formData={formData} setFormData={setFormData} />;
          }
          if (activeTab === 'billing') {
            return <TabComponent contact={contact} billing={billing} />;
          }
          if (activeTab === 'notes') {
            return <TabComponent contact={contact} onUpdate={onUpdate} />;
          }

          return <TabComponent contact={contact} />;
        })()}
        </div>
      </div>
    </div>
  );

  return isQuickView ? quickView : fullDetailView;
};

export default ContactDetail;
